import { useEffect, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, IconButton, List, ListItem, ListItemButton, ListItemText, TextField, Typography,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import { useAuth } from "src/context/AuthContext";
import { useT } from "src/i18n";
import { captureError } from "src/utils/errorReporting";
import { fetchImpersonationFavorites, removeImpersonationFavorite } from "src/utils/auth/impersonateApi";

const fullName = (p) => [p.personFirstName, p.personLastName].filter(Boolean).join(" ").trim();

// Staff-only: pick a saved target or enter the Jerry customer's email to
// impersonate them. The mint is server-gated by canImpersonate; an unauthorized
// target returns 403 -> inline error. The saved list is this app's OWN favorites
// (personContactTypeId=2), never the operator's betoni.online list.
export default function ImpersonateDialog({ open, onClose }) {
  const { impersonate, adminToken } = useAuth();
  const t = useT();
  const [email, setEmail] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [favorites, setFavorites] = useState([]);
  const [loadingFavs, setLoadingFavs] = useState(false);

  // All setState stays inside the inner run() — the codebase's
  // react-hooks/set-state-in-effect workaround (see NavSignalsProvider).
  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!adminToken) return;
      setLoadingFavs(true);
      try {
        const favs = await fetchImpersonationFavorites(adminToken);
        if (!cancelled) setFavorites(favs);
      } catch (err) {
        captureError(err, { tags: { feature: "auth", operation: "impersonate-favorites-load" } });
      } finally {
        if (!cancelled) setLoadingFavs(false);
      }
    }
    run();
    return () => { cancelled = true; };
  }, [adminToken]);

  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onClose();
    } catch (err) {
      // 403 = not allowed to impersonate this person; 404 = no such person
      // (a typo in the email field). Both are answers to the admin's input, not
      // defects — show them inline and keep them out of Sentry.
      const expected = err?.status === 403 || err?.status === 404;
      if (err?.status === 403) setError(t("auth.impersonate.forbidden"));
      else if (err?.status === 404) setError(t("auth.impersonate.notFound"));
      else setError(t("auth.impersonate.genericFailed"));
      if (!expected) captureError(err, { tags: { feature: "auth", operation: "impersonate-start" } });
    } finally {
      setBusy(false);
    }
  };

  const submit = () => run(() => impersonate({ email: email.trim() }));
  const launch = (personId) => run(() => impersonate({ personId }));

  const removeFavorite = async (targetPersonId) => {
    try {
      await removeImpersonationFavorite({ token: adminToken, targetPersonId });
      setFavorites((prev) => prev.filter((f) => f.contactPersonId !== targetPersonId));
    } catch (err) {
      captureError(err, { tags: { feature: "auth", operation: "impersonate-favorites-remove" } });
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("auth.impersonate.dialogTitle")}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loadingFavs ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 1 }}><CircularProgress size={20} /></Box>
        ) : favorites.length > 0 ? (
          <>
            <Typography variant="caption" sx={{ color: "text.secondary", px: 0.5 }}>{t("auth.impersonate.savedLabel")}</Typography>
            <List dense sx={{ py: 0 }}>
              {favorites.map((f) => (
                <ListItem
                  key={f.contactPersonId}
                  disablePadding
                  secondaryAction={
                    <IconButton edge="end" size="small" aria-label={t("auth.shared.removeFavorite")} disabled={busy} onClick={() => removeFavorite(f.contactPersonId)}>
                      <StarIcon fontSize="small" sx={{ color: "warning.main" }} />
                    </IconButton>
                  }
                >
                  <ListItemButton disabled={busy} onClick={() => launch(f.contactPersonId)} sx={{ borderRadius: 1 }}>
                    <ListItemText
                      primary={fullName(f) || f.personEmail}
                      secondary={fullName(f) ? f.personEmail : undefined}
                      slotProps={{ primary: { noWrap: true }, secondary: { noWrap: true } }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            <Divider sx={{ my: 1 }} />
          </>
        ) : null}

        <TextField
          autoFocus fullWidth type="email" label={t("auth.impersonate.emailLabel")}
          value={email} onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && email.trim()) submit(); }}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{t("auth.shared.cancel")}</Button>
        <Button onClick={submit} variant="contained" disabled={busy || !email.trim()}>{t("auth.shared.loginButton")}</Button>
      </DialogActions>
    </Dialog>
  );
}
